import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import RoomSheet from './RoomSheet';
import { chatGameApi } from './chatGameApi';
import { isRealHuman } from '../../utils/playerKind';
import {
  EXPORT_DEFAULTS, EXPORT_FOOTER, EXPORT_PRIVACY_LINE, EXPORT_LINE_CAP,
  rangeBounds, gatherChat, toMarkdown, toJson, exportFileName, downloadText,
} from '../../utils/chatExport';

// ⬇️ Export this chat (SPACES_REDESIGN_BRAINSTORM_2026_09_29 §5.10, mockup room-toolkit.html
// §5 "One dialog with toggles"). Everything is built in the browser from the paged messages
// reader (utils/chatExport.js holds the rules: what goes in, what never does). No backend
// change, no new route.
// Who can open it is the page's call (default taken 09-29: anyone who can read the room).
// `isSecretGhost` (competitive / imposter): the side-chat and AI-thoughts switches are not
// rendered at all, and the file never marks who is an AI.
// `viewer`: the seat the page reads as (humanPlayerId), or { playerId }; chatGameApi proves
// it with the stored seat secret, so a DM reaches the file only when it is really yours.
// `users` (optional): the page's seat list, only to mark "(AI)" lines the page already
// shows as AIs (utils/playerKind: `is_human` is not "a person").

const SWITCHES = [
  { k: 'main', n: 'Main chat', d: 'Everything said in the room.' },
  { k: 'side', n: 'Side chat, the ghost crowd', d: 'The crowd’s lines on the side.', secretOff: true },
  { k: 'files', n: 'Files as links', d: 'Names, summaries and links. Not the files.' },
  { k: 'thoughts', n: 'AI thoughts', d: 'What the AIs thought but didn’t say.', secretOff: true, needsMain: true },
];
const RANGES = [{ k: 'week', n: 'Last 7 days' }, { k: 'all', n: 'Everything' }, { k: 'custom', n: 'Custom' }];
const FORMATS = [{ k: 'md', n: 'Markdown' }, { k: 'json', n: 'JSON' }];

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const n0 = (n) => Number(n || 0).toLocaleString('en-US');

export default function ExportChatDialog({
  open, onClose, gameId, roomName, viewer = null, isSecretGhost = false, users = null,
  api = chatGameApi, isMobile = false, accent = null,
}) {
  const [inc, setInc] = useState({ main: EXPORT_DEFAULTS.main, side: EXPORT_DEFAULTS.side, files: EXPORT_DEFAULTS.files, thoughts: EXPORT_DEFAULTS.thoughts });
  const [range, setRange] = useState(EXPORT_DEFAULTS.range);
  const [format, setFormat] = useState(EXPORT_DEFAULTS.format);
  const [from, setFrom] = useState(() => ymd(new Date(Date.now() - 7 * 24 * 3600 * 1000)));
  const [to, setTo] = useState(() => ymd(new Date()));
  const [phase, setPhase] = useState('idle'); // idle | busy | done | error
  const [count, setCount] = useState(0);
  const [note, setNote] = useState('');
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const runRef = useRef(null); // { aborted } of the gather in flight
  const busyRef = useRef(false); // synchronous: a double tap never starts two gathers

  // a fresh open starts clean (the switches keep what you picked last time)
  useEffect(() => {
    if (open) { setPhase('idle'); setNote(''); setCount(0); }
    return () => { if (runRef.current) runRef.current.aborted = true; busyRef.current = false; };
  }, [open]);

  const secret = !!isSecretGhost;
  const shown = SWITCHES.filter((s) => !(secret && s.secretOff));
  const want = {
    main: inc.main,
    side: !secret && inc.side,
    files: inc.files,
    thoughts: !secret && inc.main && inc.thoughts,
  };
  const nothingOn = !want.main && !want.side && !want.files;
  const bounds = range === 'custom' ? rangeBounds('custom', { from, to }) : rangeBounds(range, { now: Date.now() });
  const aiSenderIds = useMemo(() => {
    if (secret || !Array.isArray(users)) return null;
    const ids = new Set();
    for (const u of users) if (u && u.id != null && !isRealHuman(u)) ids.add(u.id);
    return ids;
  }, [users, secret]);
  const viewerId = viewer && typeof viewer === 'object' ? (viewer.playerId || null) : (viewer || null);
  const busy = phase === 'busy';
  const canGo = !busy && !nothingOn && !!bounds && !!gameId;

  const go = async () => {
    if (busyRef.current || !canGo) return;
    busyRef.current = true;
    const run = { aborted: false };
    runRef.current = run;
    setPhase('busy'); setCount(0); setNote('');
    try {
      const result = await gatherChat({
        api, gameId, viewerId, include: want, bounds, secret, aiSenderIds,
        signal: run, onProgress: ({ lines }) => { if (!run.aborted) setCount(lines); },
      });
      if (run.aborted) return;
      const meta = { roomName, range, include: want, bounds, secret, now: new Date() };
      const text = format === 'json' ? toJson(result, meta) : toMarkdown(result, meta);
      downloadText(text, exportFileName(roomName, format, meta.now), format);
      setCount(result.lines.length);
      setNote(result.lines.length
        ? `Saved ${n0(result.lines.length)} line${result.lines.length === 1 ? '' : 's'}.${result.trimmed ? ` The newest ${n0(EXPORT_LINE_CAP)}.` : ''}`
        : 'Nothing in that range. Saved an empty file.');
      setPhase('done');
    } catch (err) {
      if (run.aborted || (err && err.name === 'AbortError')) return;
      setNote('That didn’t download. Try again?');
      setPhase('error');
    } finally {
      if (runRef.current === run) runRef.current = null;
      busyRef.current = false;
    }
  };

  const cancel = () => {
    if (runRef.current) { runRef.current.aborted = true; runRef.current = null; }
    busyRef.current = false;
    setPhase('idle'); setNote('');
    if (!busy && onClose) onClose();
  };

  const status = busy
    ? `Gathering ${n0(count)} line${count === 1 ? '' : 's'}…`
    : phase === 'done' || phase === 'error' ? note
      : nothingOn ? 'Turn something on.'
        : !bounds ? 'Pick both dates.'
          : '';

  return (
    <RoomSheet
      open={open}
      onClose={() => { if (runRef.current) runRef.current.aborted = true; if (onClose) onClose(); }}
      title="Export this chat"
      sub={roomName || null}
      icon="⬇"
      isMobile={isMobile}
      accent={accent}
      testId="export-chat"
      footer={(
        <>
          <span className="rsh-line"><span aria-hidden="true">♡ </span>{EXPORT_FOOTER}</span>
          <button type="button" className="rsh-act" data-prop-ui="" onClick={cancel}>{busy ? 'Stop' : phase === 'done' ? 'Close' : 'Cancel'}</button>
          <button type="button" className="rsh-act rsh-pri" data-prop-ui="" data-export-go="1" disabled={!canGo} onClick={go}>
            <span aria-hidden="true">⬇</span><span>{busy ? 'Gathering…' : 'Download'}</span>
          </button>
        </>
      )}
    >
      <div className="rsh-lb" id={`cxp-inc-${uid}`}>Include</div>
      <div role="group" aria-labelledby={`cxp-inc-${uid}`}>
        {shown.map((s) => {
          const off = s.needsMain && !inc.main;
          return (
            <div className="rsh-row" key={s.k} data-off={off ? '1' : '0'} data-export-switch={s.k}>
              <div className="rsh-tx"><b>{s.n}</b><span>{s.d}</span></div>
              <label className="rsh-sw">
                <input
                  type="checkbox"
                  role="switch"
                  aria-label={s.n}
                  checked={!!inc[s.k] && !off}
                  disabled={busy || off}
                  onChange={(e) => setInc((v) => ({ ...v, [s.k]: e.target.checked }))}
                />
                <i aria-hidden="true" />
              </label>
            </div>
          );
        })}
      </div>

      <div className="rsh-lb" id={`cxp-range-${uid}`}>Range</div>
      <div className="rsh-seg" role="radiogroup" aria-labelledby={`cxp-range-${uid}`}>
        {RANGES.map((r) => (
          <label key={r.k}>
            <input type="radio" name={`cxp-range-${uid}`} value={r.k} checked={range === r.k} disabled={busy} onChange={() => setRange(r.k)} />
            <span>{r.n}</span>
          </label>
        ))}
      </div>
      {range === 'custom' ? (
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <input className="rsh-field rsh-mono" type="date" aria-label="From" value={from} max={to || undefined} disabled={busy} onChange={(e) => setFrom(e.target.value)} />
          <input className="rsh-field rsh-mono" type="date" aria-label="To" value={to} min={from || undefined} disabled={busy} onChange={(e) => setTo(e.target.value)} />
        </div>
      ) : null}

      <div className="rsh-lb" id={`cxp-fmt-${uid}`}>Format</div>
      <div className="rsh-seg" role="radiogroup" aria-labelledby={`cxp-fmt-${uid}`}>
        {FORMATS.map((f) => (
          <label key={f.k}>
            <input type="radio" name={`cxp-fmt-${uid}`} value={f.k} checked={format === f.k} disabled={busy} onChange={() => setFormat(f.k)} />
            <span>{f.n}</span>
          </label>
        ))}
      </div>

      <div className="rsh-fine"><span aria-hidden="true">🔒</span><span>{EXPORT_PRIVACY_LINE}</span></div>
      <div className={`rsh-fine${phase === 'error' ? ' rsh-err' : ''}`} role="status" aria-live="polite" data-export-status={phase}
        style={{ color: phase === 'error' ? '#FFB4A2' : '#ECEEF2', fontWeight: 600, minHeight: 18 }}>
        {status}
      </div>
    </RoomSheet>
  );
}
